import {h, using, data, attr, text, list} from '../h'
import {AStarFinder, Heuristic} from '../graph/deps/pathfinding'

import {IS_IOS, SHOW_GRAPH} from '../env'
import {
  BLOCK_AMOUNT_IN_ROW,
  MATRIX_CELL_SIZE,
  BLOCK_WIDTH,
  BLOCK_HEIGHT,
  NODE_TEXT_MAX
} from './env'
import {compileCellList} from '../graph/road'
import {workingSet} from '../workingSet'
import {
  blockList,
  pathfinderGrid,
  fieldHeightInCells,
  filteredNodes
} from './field'
import {getBlockPortPosition} from './blockPort'
import {Rules} from './backgroundGrid'

function posFromBlockIndex(index: number): [number, number] {
  const x = index % BLOCK_AMOUNT_IN_ROW
  const y = (index - x) / BLOCK_AMOUNT_IN_ROW
  return [x, y]
}

function findPath([x1, y1]: [number, number], [x2, y2]: [number, number]) {
  const path = new AStarFinder({
    allowDiagonal: false,
    // dontCrossCorners: true,
    heuristic: Heuristic.chebyshev
  }).findPath(x1, y1, x2, y2, pathfinderGrid.getState().clone())
  if (path.length === 0) throw Error('no path found')
  if (path.length < 2) throw Error('path too short')
  return path
}

function findBlockPos(nodes, id) {
  return posFromBlockIndex(nodes.findIndex(node => node.id === id))
}

const edges$ = workingSet
  .map(({nodes, edges}) => {
    return edges.map(edge => {
      const source = findBlockPos(nodes, edge.source)
      const target = findBlockPos(nodes, edge.target)
      const out1 = getBlockPortPosition(source, 'out1')
      const in1 = getBlockPortPosition(target, 'in1')
      const path = findPath(out1, in1)
      const pointPath = path.map(([x, y]) => ({x, y}))
      return pointPath
    })
  })
  .map(paths =>
    compileCellList(paths, {
      cellPadding: 4,
      directionPadding: 0,
      linePadding: 3,
      linesPerDirection: 10,
      roundPadding: 4
    })
  )
export function GraphView() {
  if (SHOW_GRAPH) {
    h('div', () => {
      data({section: 'graph'})
      h('svg', () => {
        attr({
          id: 'graph',
          height: fieldHeightInCells.map(height => height * MATRIX_CELL_SIZE)
        })
        if (!IS_IOS) {
          if (typeof document !== 'undefined') {
            attr({width: document.body.clientWidth - 8 * 2})
          }
        }
        Defs()
        Rules()
        Cells()
        Edges()
        Labels()
      })
    })
  }
}
function Defs() {
  h('defs', () => {
    h('marker', () => {
      attr({
        id: 'arrow',
        markerWidth: 6,
        markerHeight: 6,
        refX: 1.5,
        refY: 2,
        orient: 'auto',
        markerUnits: 'strokeWidth',
        fill: '#120309'
      })
      h('circle', () => {
        attr({
          cx: 1.5,
          cy: 2,
          r: 1.5
        })
      })
    })
    h('marker', () => {
      attr({
        id: 'arrow-dot',
        markerWidth: 6,
        markerHeight: 6,
        refX: 2,
        refY: 2,
        orient: 'auto',
        markerUnits: 'strokeWidth',
        fill: '#fffcfa',
        stroke: '#120309',
        'stroke-width': 0.5
      })
      h('circle', () => {
        attr({
          cx: 2,
          cy: 2,
          r: 1.5
        })
      })
    })
  })
}

function Labels() {
  h('g', () => {
    data({layer: 'label'})
    list(filteredNodes, ({store, index}) => {
      const blockPos = posFromBlockIndex(index)
      return h('g', () => {
        h('text', () => {
          const portPos = getBlockPortPosition(blockPos, 'text-top')
          const text_ = store.map(({label}) => label)
          const labelSize = text_.map(text => {
            if (text.length > 7) return 'big-1'
            if (text.length > 5) return 'big-2'
            return 'big-3'
          })
          attr({
            x: portPos[0] * MATRIX_CELL_SIZE,
            y: portPos[1] * MATRIX_CELL_SIZE,
            'dominant-baseline': 'hanging',
            'text-anchor': 'start'
          })
          data({
            color: 'black',
            fieldLabel: true,
            labelSize
          })
          text(text_)
        })
        h('text', () => {
          const portPos = getBlockPortPosition(blockPos, 'text-center')
          const text_ = store.map(node => {
            if ('unit' in node) return node.unit
            if ('op' in node) {
              switch (node.op) {
                case 'watch':
                  if (node.sources.length > NODE_TEXT_MAX) {
                    return `${node.sources.slice(0, NODE_TEXT_MAX - 3)}...`
                  }
                  return node.sources
              }
              return node.op
            }
            return ''
          })
          attr({
            x: portPos[0] * MATRIX_CELL_SIZE,
            y: portPos[1] * MATRIX_CELL_SIZE,
            'dominant-baseline': 'hanging',
            'text-anchor': 'start'
          })
          data({
            color: 'black',
            fieldLabel: true,
            labelWeight: 'thin'
          })
          text(text_)
        })
      })
    })
  })
}

function Cells() {
  h('g', () => {
    data({layer: 'cells'})
    list(blockList, ({store}) => {
      return h('rect', () => {
        data({fieldCell: 'stroke', color: 'black'})
        const x = store.map(({cell: {x}}) => x * MATRIX_CELL_SIZE)
        const y = store.map(({cell: {y}}) => y * MATRIX_CELL_SIZE)
        attr({
          width: BLOCK_WIDTH * MATRIX_CELL_SIZE,
          height: BLOCK_HEIGHT * MATRIX_CELL_SIZE,
          x,
          y
        })
      })
    })
  })
}

function Edges() {
  h('g', () => {
    data({layer: 'edges'})
    list(edges$, ({store}) => {
      return h('path', () => {
        attr({
          d: store,
          'marker-start': 'url(#arrow-dot)',
          'marker-end': 'url(#arrow)'
        })
      })
    })
  })
}
